"use client";

import { useState } from "react";
import type { GoalMode } from "@/lib/api";

type ShapeOption = {
  key: GoalMode;
  label: string;
  helper: string;
};

const OPTIONS: ShapeOption[] = [
  {
    key: "project",
    label: "Project",
    helper: "Long focused sessions, fewer per week — protect time for deep work.",
  },
  {
    key: "habit",
    label: "Habit",
    helper: "Short daily sessions anchored to a routine — keep the streak going.",
  },
  {
    key: "direction",
    label: "Direction",
    helper: "Open exploration, opportunistic blocks — gives way to higher-priority work.",
  },
];

interface Props {
  goalId: string;
  mode: GoalMode;
  disabled?: boolean;
  onChange: (next: GoalMode) => Promise<void> | void;
}

export default function GoalShapeToggle({ goalId, mode, disabled, onChange }: Props) {
  const [pending, setPending] = useState<GoalMode | null>(null);
  const [hovered, setHovered] = useState<GoalMode | null>(null);

  const helperFor = hovered ?? mode;
  const helper = OPTIONS.find((o) => o.key === helperFor)?.helper ?? "";

  const handleClick = async (next: GoalMode) => {
    if (next === mode || pending) return;
    setPending(next);
    try {
      await onChange(next);
    } finally {
      setPending(null);
    }
  };

  return (
    <div data-testid={`goal-shape-toggle-${goalId}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
        Goal shape
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {OPTIONS.map((option) => {
          const isActive = option.key === mode;
          const isLoading = pending === option.key;
          return (
            <button
              key={option.key}
              type="button"
              data-testid={`goal-shape-${goalId}-${option.key}`}
              aria-pressed={isActive}
              disabled={disabled || Boolean(pending)}
              onMouseEnter={() => setHovered(option.key)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(option.key)}
              onBlur={() => setHovered(null)}
              onClick={() => void handleClick(option.key)}
              className={`rounded-full px-3 py-1.5 text-[10px] font-semibold transition-colors ${
                isActive
                  ? "bg-forest-med text-white"
                  : "border border-border bg-white text-text-light hover:bg-sage-pale"
              } ${isLoading ? "cursor-wait opacity-70" : ""}`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-muted">{helper}</p>
    </div>
  );
}
